// ─────────────────────────────────────────────────────────────────────────
//  ClickViewer · web · Mantine 테마 — 색 램프 · 글자 · 간격 · 부품 규격
//
//  ★★ 값이 사는 곳이 **둘**이고, 경계가 분명하다. 섞지 마라.
//
//      global.css  … 화면의 **표면색** (--cv-ink · --cv-line · --cv-danger …)
//                    그리고 그것을 Mantine 이름(--mantine-color-*)에 연결하는 일
//      theme.ts    … **색 램프**(Mantine 이 JS 로 variant 색을 계산하려면 필요하다) ·
//                    글자 크기 · 행간 · 간격 · 분기점 · 부품 기본값
//
//    두 파일은 **같은 값을 두 번 적지 않는다.** global.css 는 아래 램프에서 골라 쓴다:
//        --cv-danger:    var(--mantine-color-danger-7);   ← 아래 danger[7] 바로 그것
//        --cv-danger-bg: var(--mantine-color-danger-0);   ← 아래 danger[0]
//    그래서 색을 바꿀 때 고칠 곳은 여전히 한 군데(여기)다.
//
//    ⚠️ 왜 `cssVariablesResolver` 를 안 쓰는지는 **global.css §0** 에 적어 뒀다. 요약:
//       그건 main.tsx 가 <MantineProvider cssVariablesResolver={…}> 로 넘겨 줘야만 도는데,
//       **안 넘기면 한 줄도 안 먹고 증상이 "회색이 좀 다르네" 라서 아무도 안 찾는다.**
//       (실제로 지금 main.tsx 는 안 넘긴다.) 배선이 필요한 장치는 배선을 잊는다.
//
//  ★★★ 이 시스템의 제1원칙: **명암으로 위계를 만들지 마라.** ★★★
//    햇빛(20,000lux) 실측에서 본문 대비가 16.56:1 → 3.66:1 로, 흐린 글자가 4.37:1 →
//    2.48:1 로 **같이 뭉개진다.** 반사광은 두 색에 같은 양이 더해지므로 대비가 깎이는 게
//    아니라 **눌린다.** 실내에서 9배 차이 나던 두 글자가 밖에서는 1.5배가 된다.
//    직사광(100,000lux)에서는 순수 검정조차 1.91:1 이다.
//    → 위계는 **크기·굵기·자리**로 만든다. 그것만 야외에서 살아남는다.
//
//  ★ 야외 등급 — 새 화면을 만들 때 이 표로 자가 검사해라(검사할 수 없는 규칙은 안 지켜진다).
//
//      A · 야외 필수 : --cv-ink 또는 --cv-ink-2, 16px 이상, weight ≥ 600
//                      → 오류 문구 · 지금 로그인한 주소 · slug · 주동작 버튼 · 연락처 ·
//                        되돌릴 수 없다는 경고
//      B · 실내 보조 : --cv-muted 허용, 14px 이상
//                      → 표 머리글 · 단위 · 시각
//      금지          : **12px + muted 조합.** 어디에도 쓰지 마라.
//
//    ⚠️ `--cv-muted` 는 **라벨 전용**이다. 정보(주소·slug·좌표계·연락처)에 쓰지 마라 —
//       흰 배경에서 6.31:1 이지만 야외에서는 2.86:1 이라 사실상 안 보인다.
// ─────────────────────────────────────────────────────────────────────────

import { createTheme, type MantineColorsTuple } from '@mantine/core';

// ═══ 색 램프 ══════════════════════════════════════════════════════════════
//
//  측량 계측기·검측조서의 색이다. 짙은 청록 하나 + 의미색 **셋**.
//
//  ⚠️ 강조색(무엇을 누를 수 있나)과 의미색(상태가 어떤가)은 **절대 겹치지 않는다.**
//     겹치면 "초록이니까 정상인가, 누르라는 건가" 를 사용자가 매번 판단해야 한다.
//
//  ★ warn 과 danger 를 **나눴다.** 옛 설계는 주의색 하나였다. 그런데 이 제품에는
//    되돌릴 수 없는 동작이 실제로 있다 — 현장 정지 · 구성원 회수 · 초대 무효화 ·
//    slug 영구 점유. "다시 하면 되는 일"과 "못 되돌리는 일"이 같은 색이면 사람이
//    그 둘을 구분할 방법이 화면에 없다.
//
//  ★ 램프를 **Mantine 색으로 등록**해야 하는 이유는 둘이다:
//      ① `<Alert color="danger">` `<Button color="danger">` 처럼 이름으로 쓸 수 있다.
//      ② Mantine 이 `--mantine-color-danger-0..9` 를 CSS 변수로 내보내므로
//         global.css 가 그걸 골라 쓸 수 있다 = **hex 를 두 번 안 적는다.**
//
//  ⚠️ 「이름으로 쓸 수 있으니 `color="red"` 우회가 막힌다」고 여기 적혀 있었다. **틀렸다.**
//     쓸 수 있게 만든 것과 못 쓰게 막은 것은 다르다. 실측 [2026-08-07]:
//       · `<Alert color="red">` 9곳이 조용히 **초록 안내 상자**로 나가고 있었다(모르는 이름 폴백).
//       · `<Button color="red">` 2곳은 Mantine 기본 red = **4.51:1** 그대로였다.
//       · 한 확인창 안에서 **경고는 초록, 그 아래 파괴적 버튼은 분홍**이었다.
//     → 지금은 세 겹이다: 아래 `colors` 의 red/orange **별칭**(그물) · `alertTone`/Button 의
//       **개발용 경고**(짖는다) · components/Notice 의 **타입 강제**(정식 통로).
//
//  각 램프의 **7번 = 밝은 화면 값, 4번 = 어두운 화면 값, 0번 = 밝은 화면 배경색**이다.
//  ⚠️ primaryShade({light:7, dark:4})와 짝이다. 순서를 바꾸면 버튼 색이 통째로 바뀐다.

/** 강조색. 7번 #0E6B63(흰 위 6.36) · 4번 #57C4AC(어두운 면 위 7.76) */
const cv: MantineColorsTuple = [
  '#E6F1EE', '#C9E4DD', '#A3D3C7', '#7ACBB8', '#57C4AC',
  '#3E9C86', '#238373', '#0E6B63', '#0B554F', '#073F3A',
];

/** 위험 — **되돌릴 수 없는 것.** 7번 #A32218 (흰 7.50 / 위험바탕 6.44) */
const danger: MantineColorsTuple = [
  '#FBEAE8', '#F6D3CF', '#EFB4AD', '#F0A096', '#F08A7E',
  '#D9584A', '#C13A2C', '#A32218', '#85180F', '#661009',
];

/** 주의 — **다시 하면 되는 것.** 7번 #A8431A (흰 6.03 / 주의바탕 5.26) */
const warn: MantineColorsTuple = [
  '#FAEDE6', '#F4D9C9', '#EDBFA3', '#E9A377', '#E4834F',
  '#CC6733', '#B85426', '#A8431A', '#8A3512', '#6B280C',
];

/** 정상. 7번 #166B43 (흰 6.53 / 정상바탕 5.65) */
const ok: MantineColorsTuple = [
  '#E4F2EA', '#C6E6D3', '#9DD5B4', '#7ECBA0', '#5FC08C',
  '#3FA36D', '#268856', '#166B43', '#0F5334', '#093C26',
];

/** 우리가 쓰는 **뜻 이름 넷.** 이것 말고는 없다. */
const TONE = {
  info: { fg: 'var(--cv-accent)', bg: 'var(--cv-accent-bg)' },
  ok: { fg: 'var(--cv-ok)', bg: 'var(--cv-ok-bg)' },
  warn: { fg: 'var(--cv-warn)', bg: 'var(--cv-warn-bg)' },
  danger: { fg: 'var(--cv-danger)', bg: 'var(--cv-danger-bg)' },
} as const;
type ToneName = keyof typeof TONE;

/** `<Alert color="…">` 로 들어와도 되는 이름. (`cv` = components/Notice 의 info) */
const ALERT_TONE: Record<string, ToneName> = {
  cv: 'info',
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
};

/**
 * Mantine 기본 이름 → 우리 뜻.
 *
 * ★ 아래 `colors` 의 `red: danger` · `orange: warn` 과 **같은 매핑**이다. 두 곳이
 *   같아야 `<Alert color="red">` 와 `<Button color="red">` 가 한 화면에서 다른 색으로
 *   나오는 일이 없다 — 실제로 그게 났었다(경고는 초록, 그 아래 버튼은 분홍).
 */
const LEGACY_ALIAS: Record<string, ToneName> = { red: 'danger', orange: 'warn' };

/**
 * ★★ **조용한 폴백이 이 결함의 원인이었다.** ★★
 *
 * 옛 코드는 모르는 색 이름을 말없이 info(초록)로 떨어뜨렸다. 그래서 `<Alert color="red">`
 * 아홉 자리가 **전부 초록 안내 상자**로 나가는 동안 아무 신호도 없었다. 「위험」이라고
 * 적힌 상자가 「정상」 색으로 그려지는 것은 이 제품에서 제일 위험한 종류의 조용한 고장이다.
 *
 * → 이제 색은 **맞게 그리되**(별칭이 있으면 그 뜻으로) 개발 중에는 **시끄럽게** 짖는다.
 *   breakpoint.ts 의 CSS/JS 분기점 검사와 같은 규율이다: 사람이 기억으로 지키는 규칙은
 *   안 지켜진다. 기계가 잡아야 한다.
 *
 * ⚠️ 한 번만 짖는다. `vars` 는 렌더마다 도는데 매번 찍으면 콘솔이 덮여서
 *    **결국 아무도 안 본다** = 안 짖는 것과 같아진다.
 * ⚠️ 실서버 빌드에서는 `import.meta.env.DEV` 가 false 라 통째로 사라진다.
 */
const shouted = new Set<string>();
function shoutOnce(key: string, message: string): void {
  if (!import.meta.env.DEV || shouted.has(key)) return;
  shouted.add(key);
  // oxlint-disable-next-line no-console
  console.error(message);
}

function alertTone(color: string | undefined): { fg: string; bg: string } {
  if (!color) return TONE.info;

  const known = ALERT_TONE[color];
  if (known) return TONE[known];

  const alias = LEGACY_ALIAS[color];
  if (alias) {
    shoutOnce(
      `alert:${color}`,
      `[cv] <Alert color="${color}"> 가 있다. 색은 우리 ${alias} 로 그렸지만 ` +
        `**raw <Alert> 를 쓰지 마라** — components/Notice.tsx 의 <Notice tone="${alias}"> 로 바꿔라. ` +
        'tone 은 타입으로 강제되고 아이콘·굵기·aria-live 까지 한 벌로 온다.',
    );
    return TONE[alias];
  }

  shoutOnce(
    `alert:${color}`,
    `[cv] <Alert color="${color}"> — 모르는 색 이름이라 info(강조색)로 그렸다. ` +
      '「위험」이라고 적힌 상자가 「정상」 색으로 나가는 사고가 여기서 났었다. ' +
      'components/Notice.tsx 의 tone(info·ok·warn·danger) 중 하나를 써라.',
  );
  return TONE.info;
}

// ═══ 테마 ═════════════════════════════════════════════════════════════════

export const theme = createTheme({
  /**
   * ★★ `red` 와 `orange` 를 **우리 램프로 착지시킨다.** ★★
   *
   * 위 Alert 의 `vars` 덮어쓰기는 **Alert 에만** 걸린다. `<Button color="red">` ·
   * `<Badge color="red">` · `<Menu.Item color="red">` 는 그 통로를 안 지나가므로
   * Mantine **기본 red** 로 그려졌다 — 실측 `#c92a2a` on `#ffe3e3` = **4.51:1**,
   * Notice.tsx 가 「AA 를 0.01 로 넘긴다」고 적어 둔 바로 그 숫자다.
   * 문서에는 고쳐졌다고 적혀 있었지만 코드에는 남아 있었다(Members 의 내보내기 버튼 둘).
   *
   * 여기 한 줄이면 **부품 종류와 무관하게** 우리 색이 나온다. 놓친 자리가 생겨도
   * 4.51 이 아니라 danger(6.44) · warn(5.26)로 착지한다.
   *
   * ⚠️ 그렇다고 `color="red"` 가 **정식 통로가 된 것은 아니다.** 여기는 그물이다.
   *    화면에서는 뜻 이름(`danger`·`warn`·`ok`)이나 components/Notice 를 써라 —
   *    Alert·Button 은 개발 모드에서 별칭을 보면 콘솔에 대고 짖는다(shoutOnce).
   */
  colors: { cv, danger, warn, ok, red: danger, orange: warn },
  primaryColor: 'cv',
  primaryShade: { light: 7, dark: 4 },

  /**
   * ★ **CDN 웹폰트를 쓰지 않는다.** 실서버에서 CSP 를 조이는 날 통째로 막히고,
   *   그때 증상은 "글자가 갑자기 못생겨짐" 이라 아무도 원인을 안 찾는다.
   *   또 현장 LTE 음영에서 폰트 200KB 를 기다리는 동안 글자가 아예 안 보인다(FOIT).
   *
   * ★ 한글은 **폰트마다 실제 글자 크기가 다르다.** Malgun Gothic 은 같은 px 에서
   *   Pretendard 보다 작아 보인다. 크기 사다리를 넉넉하게 잡은 이유가 그것이다.
   *   맑은 고딕을 한글 이름으로도 적은 것은 윈도우 로케일에 따라 영문명이 안 잡히기 때문이다.
   */
  fontFamily:
    '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Malgun Gothic", "맑은 고딕", "Apple SD Gothic Neo", system-ui, sans-serif',
  fontFamilyMonospace:
    '"D2Coding", ui-monospace, SFMono-Regular, "Cascadia Mono", Consolas, monospace',

  /**
   * ★ 글자 크기 **5단. 이 밖의 값을 쓰지 마라.**
   *   옛 코드에 흩어져 있던 13·17·18·22px 은 전부 이 다섯 중 하나로 흡수했다.
   *   왜 5단인가: 야외에서 색 차이가 사라지므로 **크기가 위계의 주력**이 된다.
   *   단이 많으면 인접한 두 단이 서로 구분이 안 돼서 위계가 오히려 무너진다.
   *
   *   xs 12px  배지·단위                      ⚠️ muted 와 **같이 쓰지 마라**(야외 2.86:1)
   *   sm 14px  보조 정보 · 표 머리글           (--cv-ink-2 이상)
   *   md 16px  본문 기준선·항목 이름·버튼      ← iOS 자동확대 하한이기도 하다
   *   lg 20px  섹션 제목
   *   xl 28px  화면 제목                       (화면당 1회)
   */
  fontSizes: {
    xs: '0.75rem', sm: '0.875rem', md: '1rem', lg: '1.25rem', xl: '1.75rem',
  },

  /**
   * 한글은 받침 때문에 글자 하나가 세로로 꽉 찬다. 라틴 기준 행간(1.4)을 그대로 쓰면
   * 줄이 서로 붙어 보인다. → 본문 1.65, 촘촘한 표는 1.45.
   */
  lineHeights: { xs: '1.4', sm: '1.45', md: '1.65', lg: '1.5', xl: '1.25' },

  headings: {
    fontWeight: '700',
    sizes: {
      h1: { fontSize: '1.75rem', lineHeight: '1.25' },   // = xl
      h2: { fontSize: '1.25rem', lineHeight: '1.35' },   // = lg  (옛 22px 폐기)
      h3: { fontSize: '1rem', lineHeight: '1.5' },       // = md  (옛 17px 폐기)
    },
  },

  // 토목 현장 도구는 둥글둥글할 이유가 없다. 그림자도 안 쓴다 — 면은 1px 선으로 나눈다.
  // (야외에서 그림자는 제일 먼저 사라지는 신호다. 선은 남는다.)
  radius: { xs: '2px', sm: '3px', md: '3px', lg: '4px', xl: '6px' },
  defaultRadius: 'sm',

  /**
   * 간격 = **4px 배수**. 어디에 무엇을 쓰는지 규칙:
   *   xs  4px  — 같은 뜻의 조각끼리 (숫자와 단위, 배지와 배지)
   *   sm  8px  — 한 줄 안의 항목끼리 / **터치 대상 사이 최소 간격**
   *   md 12px  — 카드 안쪽 여백, 목록 항목 사이
   *   lg 16px  — 카드와 카드, 문단과 문단
   *   xl 24px  — 섹션과 섹션
   * ⚠️ 8px 배수로 하면 「숫자와 단위」 사이의 4px 자리를 만들 수 없다. 그래서 4px 배수다.
   * ⚠️ 자리마다 즉흥으로 고르지 마라. 옛 화면이 그래서 리듬이 없었다.
   */
  spacing: { xs: '4px', sm: '8px', md: '12px', lg: '16px', xl: '24px' },

  /**
   * ★★ 폰/데스크톱 분기를 **sm(768px) 하나로** 통일한다.
   *
   *   옛 코드에는 분기점이 **넷**이었다:
   *     576px  Shell.tsx `visibleFrom="xs"`
   *     720px  config.ts `PHONE_MAX_WIDTH_PX` → useIsPhone()
   *     768px  여기(통일 목표라고 선언만 해 둔 값)
   *     820px  SCREEN-DESIGN.html 목업의 `.mock-body` 접힘
   *
   *   구체적 파손: 폭 750px(세로 아이패드·폴드 펼침)에서 목업 grid 는 **한 칸으로 접히는데**
   *   `useIsPhone()` 은 **false** 라 폰용 하단 고정 바가 안 붙는다. 그러면 한 칸으로 길게
   *   늘어난 폼의 **맨 아래에 주동작이 파묻힌다.** 감리가 아이패드로 열면 못 찾는다.
   *
   *   ⚠️ 그래서 「접혔는지」는 **JS 폭 판정이 아니라 CSS 레이아웃 상태**로 정한다.
   *      global.css 의 `.cv-bottombar` 와 `.cv-form-grid` 가 **같은 미디어쿼리 안에서**
   *      같이 켜진다 — 값이 갈릴 자리가 애초에 없다. breakpoint.ts 머리말 참고.
   *
   *   768px 로 고른 이유: 세로 아이패드(768)가 데스크톱 쪽에 붙어야 표가 제 값을 한다.
   */
  breakpoints: { xs: '36em', sm: '48em', md: '62em', lg: '75em', xl: '88em' },

  components: {
    // ── 크기: 「예쁜 것」보다 「눌리는 것」 ──────────────────────────────
    // 입력 16px 미만이면 iOS 가 포커스 순간 화면을 확대하고 되돌리지 않는다. 버그다.
    TextInput: { defaultProps: { size: 'md' } },
    Textarea: { defaultProps: { size: 'md' } },
    Select: { defaultProps: { size: 'md' } },
    NativeSelect: { defaultProps: { size: 'md' } },
    PinInput: { defaultProps: { size: 'md' } },
    Checkbox: { defaultProps: { size: 'md' } },
    Switch: { defaultProps: { size: 'md' } },
    Radio: { defaultProps: { size: 'md' } },

    /**
     * ★ 채워진 버튼의 **글자색**. 어두운 화면에서 실측 **2.12:1** 이었다(AA 4.5 실패).
     *
     * 왜 그랬나: Mantine 의 기본 variant 계산은 filled 글자를 **언제나 흰색**으로 못박는다
     * (`default-variant-colors-resolver.mjs`: `autoContrast` 를 안 켜면 무조건
     *  `var(--mantine-color-white)`). 그런데 우리 `primaryShade` 는 dark 에서 **4번**이라
     * 바탕이 `cv[4] #57C4AC`(밝은 민트)다. 밝은 면에 흰 글자 = 2.12:1.
     * 「+ 사람 추가」·「만들기」·「코드 받기」·「들어가기」가 전부 그랬다.
     *
     * ⚠️ `autoContrast: true` 로는 **안 고쳐진다.** 그 계산은 `parseThemeColor` 를
     *    colorScheme 없이 부르므로 **언제나 light 의 7번**(#0E6B63, 어두움)을 보고
     *    "흰 글자" 라고 답한다. 즉 dark 에서도 흰색이 그대로 나온다. [실측 2026-08-07]
     *
     * → 우리 토큰으로 못박는다. `--cv-on-accent` 는 light `#ffffff` · dark `#0b1512` 라
     *   **램프 넷 모두**에서 맞다(7번은 어둡고 4번은 밝다는 규칙이 램프의 정의다):
     *      light  흰 on cv[7] 6.36 · danger[7] 7.50 · warn[7] 6.03 · ok[7] 6.53
     *      dark   #0b1512 on cv[4] 8.79 · danger[4] 7.68 · warn[4] 8.4 · ok[4] 8.3
     *
     * ⚠️ filled 가 아닌 variant 에는 **절대 주지 마라.** `variant="default"` 는 흰 면 위에
     *    그려지므로 같은 값을 주면 밝은 화면에서 흰 글자가 된다. 그래서 filled 만 건드린다.
     *    (Button 의 기본 variant 는 filled 라 `variant` 가 없을 때도 filled 다.)
     * ★ 왜 CSS 가 아니라 여기인가: `--button-color` 는 Mantine 이 **인라인 style** 로 내보내서
     *   어떤 클래스 규칙도 못 이긴다(global.css 의 `.cv-pin` 주석과 같은 덫). 테마의 `vars` 는
     *   부품 자신의 varsResolver **뒤에** 병합되므로(`use-styles.mjs` mergeVars) 이긴다.
     */
    Button: {
      defaultProps: { size: 'md' },
      vars: (_theme: unknown, props: { variant?: string; color?: string }) => {
        if (props.color && LEGACY_ALIAS[props.color]) {
          shoutOnce(
            `button:${props.color}`,
            `[cv] <Button color="${props.color}"> 가 있다. 색은 우리 ${LEGACY_ALIAS[props.color]} 로 ` +
              `착지시켰지만 화면에서는 뜻 이름을 써라 — color="${LEGACY_ALIAS[props.color]}". ` +
              'Mantine 기본 이름은 램프를 통째로 우회하는 길이다.',
          );
        }
        const filled = (props.variant ?? 'filled') === 'filled';
        return { root: filled ? { '--button-color': 'var(--cv-on-accent)' } : {} };
      },
    },

    ActionIcon: { defaultProps: { size: 'lg', variant: 'default' } },
    Anchor: { defaultProps: { underline: 'always' } },

    // ── 면은 선으로 나눈다. 그림자 금지 ─────────────────────────────────
    Card: {
      defaultProps: { withBorder: true, padding: 'md', radius: 'sm' },
      styles: { root: { borderColor: 'var(--cv-line)', background: 'var(--cv-surface)' } },
    },
    Paper: { defaultProps: { withBorder: true, radius: 'sm', shadow: undefined } },

    /**
     * 표 — 25번 쓰면서 테마 정의가 없었다.
     *
     * ⚠️ 머리글에서 **mono·uppercase·letter-spacing 을 뺐다.** 그건 라틴 활자 규범이고
     *    한글에는 해롭다:
     *      · 한글에는 대소문자가 없다 → `uppercase` 는 「현장」「주소」「좌표 원점대」에
     *        **아무 효과가 없는 죽은 선언**이다.
     *      · 한글에 자간 확대는 음절 블록 리듬을 깨서 낱자가 흩어져 보인다.
     *      · 게다가 12px + muted 조합은 야외 2.86:1 이라 **머리글을 못 읽는다** =
     *        어느 열이 무엇인지 모른다.
     *    → 14px · --cv-ink-2 · weight 600 · 자간 0. 「데이터가 아니라 이름」이라는 신호는
     *      **배경색(--cv-bg)과 굵기**로 충분히 난다.
     *    (라틴 전용 단위 라벨에만 mono+tracking 을 쓰고 싶으면 global.css 의 `.cv-unit` 을 써라.)
     */
    Table: {
      defaultProps: { verticalSpacing: 'sm', horizontalSpacing: 'md', highlightOnHover: true },
      styles: {
        th: {
          fontSize: '0.875rem',
          fontWeight: 600,
          letterSpacing: '0',
          textTransform: 'none',
          color: 'var(--cv-ink-2)',
          background: 'var(--cv-bg)',
          borderBottom: '1px solid var(--cv-line-strong)',
          whiteSpace: 'nowrap',
        },
        // 행 높이 48px 이상 = 터치 44px + 여백. 데스크톱에서도 같은 값을 쓴다 —
        // 감리·발주처는 **같은 표를 폰과 PC 에서 번갈아** 본다.
        td: { borderBottom: '1px solid var(--cv-line)', height: '48px' },
      },
    },

    /**
     * 배지 — **역할·상태는 색이 아니라 글자로** 말한다.
     * 옛 설계의 `--r1~--r4`(채도만 다른 초록 4단)는 야외에서도 색각 이상에서도
     * 서로 구분되지 않는다 → 폐기했다. 「감리」「소장」「발주처」라고 **쓰면**
     * 조도와 무관하게 읽힌다.
     */
    Badge: {
      defaultProps: { radius: 'sm', variant: 'default' },
      styles: {
        root: {
          fontFamily: 'var(--mantine-font-family)', fontWeight: 700,
          fontSize: '0.75rem', letterSpacing: '0.02em', textTransform: 'none',
          height: 'auto', minHeight: '22px', padding: '3px 7px',
          border: '1px solid var(--cv-line-strong)', color: 'var(--cv-ink-2)',
        },
      },
    },

    /**
     * ★ Alert — Mantine 기본 팔레트로 새는 길을 막는다.
     *
     * 옛 코드는 `<Alert color="red">` 였다. 그러면 우리 `--cv-danger`(6.44:1)가
     * **한 번도 안 쓰이고** Mantine red(4.51:1, AA 를 0.01 로 넘김)로 그려진다.
     * 즉 "danger 를 warn 에서 분리했다" 는 결정이 **문서에만 존재**했다.
     *
     * 여기서 `vars` 로 --alert-* 를 통째로 덮으므로, 어떤 color 를 넣어도 우리 색으로
     * 그려진다. 알아듣는 이름은 `cv`(=info) `ok` `warn` `danger`, 그리고 **별칭** `red`·`orange`.
     * ⚠️ 화면에서는 raw `<Alert>` 대신 **components/Notice.tsx** 를 써라 —
     *    tone 이 타입으로 강제되고 아이콘·굵기까지 한 벌로 온다. 별칭과 모르는 이름은
     *    개발 모드에서 콘솔에 대고 짖는다(alertTone 머리말).
     */
    Alert: {
      defaultProps: { variant: 'light', radius: 'sm' },
      vars: (_theme: unknown, props: { color?: string }) => {
        const t = alertTone(props.color);
        return {
          root: {
            '--alert-bg': t.bg,
            '--alert-color': t.fg,
            // ⚠️ 테두리를 **반드시** 준다. 배경만으로 구분하면 야외에서 면색이 뭉개져
            //   경고인지 본문인지 알 수 없게 된다. 선은 마지막까지 남는 신호다.
            '--alert-bd': `1px solid ${t.fg}`,
          },
        };
      },
      styles: {
        // 본문 글자를 흐리게 두지 마라 — 오류 문구는 **야외 A 등급**이다.
        message: { color: 'var(--cv-ink)', fontSize: '1rem', lineHeight: 1.65 },
        title: { fontSize: '1rem', fontWeight: 700 },
      },
    },

    /**
     * 확인창은 **드물고 되돌릴 수 없는 것**에만 쓴다(현장 만들기의 slug 확인 등).
     * 폰에서 화면 아래쪽에 뜨도록 centered 를 켜 두고, 안쪽 여백을 넉넉히 준다.
     */
    Modal: {
      defaultProps: { centered: true, radius: 'sm', padding: 'lg', overlayProps: { blur: 0 } },
      styles: { title: { fontSize: '1.25rem', fontWeight: 700 } },
    },
  },
});
